import type { ChangeEvent } from 'react';
import { Link } from 'react-admin';
import { Alert, AlertTitle, Box, Button, TextField, Typography } from '@mui/material';
import { dateTimeNoSeconds } from '../shared/dateTime';
import { pluralWord } from '../shared/plural';
import { viaWriteStatusLabel } from '../shared/status';
import {
  MIN_NOT_DUPLICATE_REASON_LENGTH,
  type ViaDuplicateCandidate,
  type ViaDuplicateCheck,
} from './viaWriteApi';

/**
 * Ostrzeżenie o duplikacie przed zapisem do VIA (16.10.2026, przełącznik
 * `viaWriteDuplicateCheck`; reguła w API: `via/duplicate-check.ts` — ta sama
 * co kandydaci pętli zwrotnej z TMS).
 *
 * Ostrzeżenie, nie blokada: operator widzi podobne zlecenia z odnośnikiem
 * i powodem dopasowania, a plik wytworzy po potwierdzeniu „to nie duplikat”
 * z powodem (historia obu zleceń). Duplikat z TMS łączy się ręcznie na
 * karcie zlecenia z TMS (przełącznik `preOrderTmsManualLink`).
 */

/** Nagłówek: ile podobnych i czy czekają na decyzję. */
export function duplicateTitle(check: ViaDuplicateCheck): string {
  const n = check.candidates.length;
  if (check.unconfirmed === 0) {
    return `Podobne ${pluralWord(n, 'zlecenie', 'zlecenia', 'zleceń')} w banku — potwierdzone: to nie duplikat`;
  }
  // „jest podobne zlecenie”, „są 2 podobne zlecenia”, „jest 5 podobnych zleceń”.
  const what =
    n === 1
      ? 'jest podobne zlecenie'
      : pluralWord(n, '', 'few', 'many') === 'few'
        ? `są ${n} podobne zlecenia`
        : `jest ${n} podobnych zleceń`;
  return `W banku ${what} — może to ten sam transport`;
}

/** Wyraźna linia: co to za zlecenie i jaka trasa. */
export function duplicateCandidateText(c: ViaDuplicateCandidate): string {
  const name =
    c.kind === 'tms'
      ? `Zlecenie z TMS ${c.number ?? `#${c.userOfferId}`}`
      : c.number
        ? `Prezlecenie ${c.number}`
        : `Prezlecenie przed akceptacją (#${c.userOfferId})`;
  const from = c.fromCity ?? c.fromCountry ?? '—';
  const to = c.toCity ?? c.toCountry ?? '—';
  return `${name}: ${from} → ${to}`;
}

/** Szara linia: terminy, klient, numer referencyjny, stan zapisu prezlecenia. */
export function duplicateCandidateDetail(c: ViaDuplicateCandidate): string {
  const parts = [`załadunek ${dateTimeNoSeconds(c.fromDate)}`, `rozładunek ${dateTimeNoSeconds(c.toDate)}`];
  if (c.clientName) parts.push(`klient: ${c.clientName}`);
  if (c.tmsReferenceNumber) parts.push(`nr ref. klienta: ${c.tmsReferenceNumber}`);
  if (c.customerOrderNumber) parts.push(`nr zamówienia klienta: ${c.customerOrderNumber}`);
  if (c.kind === 'preOrder') {
    const label = viaWriteStatusLabel(c.viaWriteStatus);
    parts.push(c.viaWriteStatus ? `zapis do VIA: ${label.charAt(0).toLowerCase()}${label.slice(1)}` : 'bez zapisu do VIA');
  }
  return parts.join('; ');
}

/** Potwierdzenie przy kandydacie. */
export function confirmationText(c: ViaDuplicateCandidate): string | null {
  const conf = c.confirmation;
  if (!conf) return null;
  const who = conf.by ? `${conf.by}, ` : '';
  return `Potwierdzono: to nie duplikat (${who}${dateTimeNoSeconds(conf.at)})${conf.reason ? ` — ${conf.reason}` : ''}`;
}

export const DUPLICATE_NOTE =
  'Spółka, dzień załadunku i rozładunku, kraje i miasta (gdy obie strony je znają) — ta sama reguła, ' +
  'po której import z TMS szuka zlecenia z prezlecenia. Plik zrobiłby w TMS drugie zlecenie, jeśli to ' +
  'ten sam transport. Sprawdź i potwierdź „to nie duplikat” z powodem — trafi do historii obu zleceń.';

export const MANUAL_LINK_NOTE =
  'Jeśli to duplikat zlecenia z TMS, nie wytwarzaj pliku — otwórz zlecenie z TMS i połącz je z tym ' +
  'prezleceniem (karta zlecenia z TMS, „Połącz”). Dwóch prezleceń nie łączymy — zostaw jedno.';

export const NO_MANUAL_LINK_NOTE =
  'Jeśli to duplikat, nie wytwarzaj pliku — zostaw jedno zlecenie.';

interface Props {
  check: ViaDuplicateCheck;
  reason: string;
  onReasonChange: (value: string) => void;
  busy: boolean;
  onConfirm: (userOfferIds: number[]) => void;
}

export const ViaWriteDuplicates = ({ check, reason, onReasonChange, busy, onConfirm }: Props) => {
  if (!check.checked) {
    return check.uncheckedReason ? (
      <Alert severity="info" sx={{ mb: 1 }} data-testid="via-duplicates-unchecked">
        {check.uncheckedReason}
      </Alert>
    ) : null;
  }
  if (check.candidates.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }} data-testid="via-duplicates-none">
        W banku nie ma podobnego zlecenia (spółka, dni załadunku i rozładunku, kraje i miasta).
      </Typography>
    );
  }
  const pending = check.candidates.filter((c) => c.confirmation === null);
  const tooShort = reason.trim().length < MIN_NOT_DUPLICATE_REASON_LENGTH;
  return (
    <Alert severity={pending.length > 0 ? 'warning' : 'success'} sx={{ mb: 1 }} data-testid="via-duplicates">
      <AlertTitle>{duplicateTitle(check)}</AlertTitle>
      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
        {check.candidates.map((c) => (
          <li key={c.userOfferId} data-testid="via-duplicate">
            <Typography variant="body2">
              <Link to={`/user-offers/${c.userOfferId}/show`}>{duplicateCandidateText(c)}</Link>
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {duplicateCandidateDetail(c)}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Zgadza się: {c.reasons.join(', ')}
            </Typography>
            {c.confirmation ? (
              <Typography variant="caption" sx={{ display: 'block' }}>
                {confirmationText(c)}
              </Typography>
            ) : (
              c.kind === 'tms' &&
              check.manualLinkAvailable && (
                <Typography variant="caption" sx={{ display: 'block' }}>
                  <Link to={`/user-offers/${c.userOfferId}/show`}>To duplikat — połącz na karcie zlecenia z TMS</Link>
                </Typography>
              )
            )}
          </li>
        ))}
      </Box>
      {pending.length > 0 && (
        <>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {DUPLICATE_NOTE}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {check.manualLinkAvailable ? MANUAL_LINK_NOTE : NO_MANUAL_LINK_NOTE}
          </Typography>
          <TextField
            label="Dlaczego to nie duplikat (wymagane, trafia do historii)"
            fullWidth
            size="small"
            value={reason}
            onChange={(e: ChangeEvent<HTMLInputElement>) => onReasonChange(e.target.value)}
            helperText={`Co najmniej ${MIN_NOT_DUPLICATE_REASON_LENGTH} znaków — np. inny klient, inny towar, inne zlecenie w mailu.`}
            sx={{ mt: 1 }}
          />
          <Button
            size="small"
            variant="outlined"
            color="warning"
            sx={{ mt: 1 }}
            disabled={busy || tooShort}
            onClick={() => onConfirm(pending.map((c) => c.userOfferId))}
          >
            To nie duplikat — zapisz powód
          </Button>
        </>
      )}
    </Alert>
  );
};
